import React, { useEffect, useState } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom-latest';
import { Modal } from 'reactstrap';
import axios from 'axios';
import { GET_DOCUMENT_DETAILS } from 'constants/constants';
import DocumentShow from 'components/UploadedFiles/DocumentShow';
import * as Utilities from 'utilities/utils';

/**
 * DirectFileViewer - Component to directly view a file by its ID
 *
 * Usage:
 * - Navigate to /files/view/:fileId
 * - Example: /files/view/12345
 * - With query string: /files/view/12345?comingFrom=onboarding
 *
 * Query Parameters:
 * - comingFrom: Optional string to indicate where the user came from
 *               (e.g., 'onboarding', 'dashboard', etc.)
 *               This is passed to DocumentShow for context-aware behavior
 */
const DirectFileViewer: React.FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [storageData, setStorageData] = useState<any>(null);

  // Extract comingFrom query parameter
  const comingFrom = searchParams.get('comingFrom');

  useEffect(() => {
    // Get storage data for years, months, tags
    const data = Utilities.getStorageData();
    setStorageData(data);

    // Fetch document details by ID
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${GET_DOCUMENT_DETAILS}/${fileId}`);

        if (response.status === 200 && response.data.data) {
          const documentData = response.data.data;
          // Remove duplicate tags
          const removeDuplicateTags = Utilities.removeDuplicateTags(
            documentData.tags,
          );
          documentData.tags = removeDuplicateTags;
          setSelectedItem(documentData);
        } else {
          setError('File not found');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load file');
      } finally {
        setLoading(false);
      }
    };

    if (fileId) {
      fetchDocument();
    }
  }, [fileId]);

  const handleClose = () => {
    // Navigate back to files list or previous page
    navigate('/files');
  };

  if (loading) {
    return (
      <div
        id="loading"
        style={{ display: 'block' }}
        role="status"
        aria-label="Loading DirectFileViewer"
      />
    );
  }

  if (error || !selectedItem) {
    return (
      <div className="center-wrapper">
        <div className="alert alert-danger" role="alert">
          {error || 'File not found'}
        </div>
        <button type="button" onClick={handleClose} className="btn btn-primary">
          Go to Files
        </button>
      </div>
    );
  }

  // Prepare data for DocumentShow component
  const documentShowData = {
    years: storageData?.pref_type_options?.upload_years || [],
    months: storageData?.pref_type_options?.upload_months || [],
    uploadTags: storageData?.pref_type_options?.upload_tags || [],
  };

  return (
    <Modal isOpen className="Md-full-width modal-FullH">
      <DocumentShow
        data={documentShowData}
        years={documentShowData.years}
        months={documentShowData.months}
        tags={documentShowData.uploadTags}
        onButtonClose={handleClose}
        selectedItem={selectedItem}
        listOfFilesState={() => {}}
        editFileName={() => {}}
        component={{
          dataAfterEditDocument: () => {},
        }}
        navigate={navigate}
        comingFrom={comingFrom}
      />
    </Modal>
  );
};

export default DirectFileViewer;
